import CheckIcon from '@mui/icons-material/Check';
import { Box, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from './StylishLoader';

interface TeamSelectMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedTeam: string;
  onSelect: (team: string, teamName?: string) => void;
}

const TeamSelectMenu: React.FC<TeamSelectMenuProps> = ({
  anchorEl,
  open,
  onClose,
  selectedTeam,
  onSelect,
}) => {
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );

  // Build query params based on user role
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    const userId = userDetail?.id;
    const role = userDetail?.teamUserRole?.toLowerCase();

    if (!userId || !role) return null;

    // Manager gets all teams (no query params)
    if (role === 'management') {
      return '';
    }

    // Business Head and Team Lead get filtered teams
    if (role === 'business_head') {
      params.append('businessHeadId', String(userId));
    } else if (role === 'team_leader') {
      params.append('teamLeadId', String(userId));
    } else if (role === 'manager') {
      params.append('managementId', String(userId));
    }

    return params.toString();
  }, [userDetail]);

  // Fetch teams data
  const endpoint = queryParams
    ? `${String(MASTER_ENDPOINT.Teams)}/team-names?${String(queryParams)}`
    : `${String(MASTER_ENDPOINT.Teams)}/team-names`;

  const { data: teamsResponse, isLoading } = useFetchList<any>(
    ['team-names', userDetail?.id, userDetail?.teamUserRole],
    endpoint,
    {
      enabled: !!open,
      refetchOnMount: 'always',
    },
  );

  // Extract team names and add "All Teams" option
  const teamsList = useMemo(() => {
    const defaultOption = { label: 'All Teams', value: '' };
    if (!teamsResponse?.data) return [defaultOption];

    const teams = teamsResponse.data.map((team: any) => ({
      label: `${String(team.teamName || team.name || '')}`,
      value: String(team.id),
    }));
    return [defaultOption, ...teams];
  }, [teamsResponse]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      className="team-select-menu"
      PaperProps={{
        className: 'team-menu-paper',
        elevation: 0,
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
      <Typography className="menu-title">View Jobs</Typography>
      <Box className="menu-scroll-container">
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: '0px',
            }}>
            <StylishLoader text="Loading teams..." mode="inline" />
          </Box>
        ) : (
          teamsList.map((team) => (
            <MenuItem
              key={team.value}
              onClick={() => onSelect(team.value, team.label)}
              className={`menu-item ${
                selectedTeam === team.value ? 'selected' : ''
              }`}>
              <ListItemIcon className="menu-icon">
                {selectedTeam === team.value && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <Typography className="menu-text">{team.label}</Typography>
            </MenuItem>
          ))
        )}
      </Box>
    </Menu>
  );
};

export default memo(TeamSelectMenu);
